import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
    Star, 
    Quote, 
    MessageSquareQuote,
    Calendar,
    MapPin,
    ThumbsUp
} from 'lucide-react';

const reviews = [
    {
        id: 1,
        name: 'Priya & Arjun Sharma',
        event: 'Wedding Reception',
        location: 'Mumbai',
        date: 'December 2024',
        rating: 5,
        content: "Hawayein made our wedding reception absolutely magical! Yash's voice gave us goosebumps, and the entire band kept our guests dancing till midnight. They played everything from romantic Bollywood classics to peppy party numbers. The coordination was seamless, and they even learned our special song request. Couldn't have asked for a better experience!",
        avatar: 'PS',
        verified: true,
    },
    {
        id: 2,
        name: 'Rahul Kapoor',
        event: 'Corporate Annual Day',
        location: 'Bangalore',
        date: 'November 2024',
        rating: 5,
        content: "We booked Hawayein for our company's annual day celebration, and they exceeded all expectations. Professional, punctual, and incredibly talented. The energy they brought to the stage got even our most reserved employees on the dance floor! The sound quality was excellent and their repertoire covered everything from retro to current hits.",
        avatar: 'RK',
        verified: true,
    },
    {
        id: 3,
        name: 'Meera Iyer',
        event: '50th Birthday Celebration',
        location: 'Chennai',
        date: 'October 2024',
        rating: 5,
        content: "Organized a surprise party for my father's 50th birthday and Hawayein was the highlight. They learned his favorite old Hindi songs and the look on his face was priceless. The band was incredibly accommodating and made sure every request was fulfilled. Thank you for making this milestone so special for our family!",
        avatar: 'MI',
        verified: true,
    },
    {
        id: 4,
        name: 'Vikram & Neha Singh',
        event: 'Sangeet Night',
        location: 'Delhi',
        date: 'September 2024',
        rating: 5,
        content: "The sangeet night wouldn't have been the same without Hawayein! They coordinated perfectly with our choreographers and made every performance segment seamless. Yash even joined for a fun segment with the bride and groom which was an absolute hit. The energy was unmatched!",
        avatar: 'VS',
        verified: true,
    },
    {
        id: 5,
        name: 'Ananya Desai',
        event: 'College Fest',
        location: 'Pune',
        date: 'August 2024',
        rating: 5,
        content: "Our college fest was a massive hit thanks to Hawayein's electrifying performance. The crowd of 3000+ students was completely mesmerized. The band's stage presence and interaction with the audience was phenomenal. They kept the energy high throughout the 3-hour set!",
        avatar: 'AD',
        verified: true,
    },
    {
        id: 6,
        name: 'Rajesh Malhotra',
        event: 'New Year Eve Party',
        location: 'Goa',
        date: 'January 2024',
        rating: 5,
        content: "Best decision we made for our New Year party in Goa! Hawayein brought the house down with their performance. The countdown and post-midnight celebration was absolutely electric. Every guest complimented us on the entertainment. Will definitely book again!",
        avatar: 'RM',
        verified: true,
    },
];

const ReviewsPage = () => {
    const averageRating = reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length;
    const totalReviews = reviews.length;

    return (
        <main className="min-h-screen pt-24 pb-16 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Page Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <MessageSquareQuote className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Reviews
                        </span>
                    </div>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        What Our Clients Say
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto mb-8">
                        Real stories from real clients who trusted us with their special moments
                    </p>

                    {/* Stats Summary */}
                    <div className="flex flex-wrap justify-center gap-6">
                        <div className="glass px-6 py-4 rounded-xl">
                            <div className="flex items-center gap-2 mb-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 fill-amber text-amber" />
                                ))}
                            </div>
                            <p className="text-2xl font-heading font-bold text-foreground">{averageRating.toFixed(1)}</p>
                            <p className="text-sm text-muted-foreground font-body">Average Rating</p>
                        </div>
                        <div className="glass px-6 py-4 rounded-xl">
                            <p className="text-2xl font-heading font-bold text-foreground">{totalReviews * 80}+</p>
                            <p className="text-sm text-muted-foreground font-body">Happy Clients</p>
                        </div>
                        <div className="glass px-6 py-4 rounded-xl">
                            <p className="text-2xl font-heading font-bold text-foreground">100%</p>
                            <p className="text-sm text-muted-foreground font-body">Satisfaction</p>
                        </div>
                    </div>
                </motion.div>

                {/* Reviews Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                    {reviews.map((review, index) => (
                        <motion.div
                            key={review.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <Card className="card-premium rounded-2xl h-full">
                                <CardContent className="p-6 flex flex-col h-full">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-foreground font-heading font-bold">
                                                {review.avatar}
                                            </div>
                                            <div>
                                                <h4 className="font-heading font-semibold text-foreground">
                                                    {review.name}
                                                </h4>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <span>{review.event}</span>
                                                    {review.verified && (
                                                        <Badge variant="outline" className="text-xs py-0 px-1.5 border-success/40 text-success">
                                                            <ThumbsUp className="w-3 h-3 mr-1" />
                                                            Verified
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <Quote className="w-8 h-8 text-primary/20" />
                                    </div>

                                    {/* Stars */}
                                    <div className="flex gap-1 mb-3">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`w-4 h-4 ${
                                                    i < review.rating
                                                        ? 'fill-amber text-amber'
                                                        : 'text-muted'
                                                }`}
                                            />
                                        ))}
                                    </div>

                                    {/* Content */}
                                    <p className="text-muted-foreground font-body text-sm leading-relaxed flex-1 mb-4">
                                        "{review.content}"
                                    </p>

                                    {/* Footer */}
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-border/30 mt-auto">
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {review.location}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {review.date}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center mt-12"
                >
                    <p className="text-muted-foreground font-body mb-4">
                        Ready to create your own unforgettable experience?
                    </p>
                    <Link to="/book">
                        <Button className="bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold px-8 py-6 rounded-xl">
                            Book Us Now
                        </Button>
                    </Link>
                </motion.div>
            </div>
        </main>
    );
};

export default ReviewsPage;
